/**
 * One-bit conversion for raster images printed on thermal label printers.
 *
 * The Brother SDK thresholds every pixel at 50% by default, which turns a
 * greyscale face into a black blob. Converting to pure black and white on
 * our side keeps the result identical on every printer and app version.
 */

export type MonoConversion = "dither" | "threshold";

const luminance = (r: number, g: number, b: number): number =>
  0.299 * r + 0.587 * g + 0.114 * b;

/** Contrast boost around mid-grey, matching the spec's `contrast(1.15)` mock. */
const PHOTO_CONTRAST = 1.15;

const toGreyscale = (
  pixels: Uint8ClampedArray,
  contrast: number
): Float32Array => {
  const grey = new Float32Array(pixels.length / 4);
  for (let i = 0; i < grey.length; i++) {
    const offset = i * 4;
    const alpha = pixels[offset + 3] / 255;
    const lum = luminance(
      pixels[offset],
      pixels[offset + 1],
      pixels[offset + 2]
    );
    // Transparent areas print as paper (white).
    const composed = lum * alpha + 255 * (1 - alpha);
    grey[i] = (composed - 128) * contrast + 128;
  }
  return grey;
};

const writeBit = (
  pixels: Uint8ClampedArray,
  index: number,
  isBlack: boolean
) => {
  const value = isBlack ? 0 : 255;
  const offset = index * 4;
  pixels[offset] = value;
  pixels[offset + 1] = value;
  pixels[offset + 2] = value;
  pixels[offset + 3] = 255;
};

/** Floyd–Steinberg error diffusion: the only way a face survives a 1-bit head. */
const ditherInPlace = (
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  grey: Float32Array
) => {
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x;
      const old = grey[index];
      const isBlack = old < 128;
      const error = old - (isBlack ? 0 : 255);
      writeBit(pixels, index, isBlack);
      if (x + 1 < width) grey[index + 1] += (error * 7) / 16;
      if (y + 1 < height) {
        if (x > 0) grey[index + width - 1] += (error * 3) / 16;
        grey[index + width] += (error * 5) / 16;
        if (x + 1 < width) grey[index + width + 1] += (error * 1) / 16;
      }
    }
  }
};

/**
 * Convert the pixels of an ImageData to pure black/white in place.
 * "dither" is for photos; "threshold" keeps logos and line art crisp.
 */
export const convertToMono = (
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  mode: MonoConversion
): void => {
  const grey = toGreyscale(pixels, mode === "dither" ? PHOTO_CONTRAST : 1);
  if (mode === "dither") {
    ditherInPlace(pixels, width, height, grey);
    return;
  }
  for (let i = 0; i < grey.length; i++) {
    writeBit(pixels, i, grey[i] < 128);
  }
};
